// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as html2pdf from "html2pdf.js";
import { Base64 } from "js-base64";
import React, { useEffect } from "react";

import { ErrorCode, getErrorCode } from "/b2c/client/utils";
import { GET_LETTERS_patient_letters_items } from "/b2c/schema";
import { List } from "/component/base";
import LoadingSkeleton from "/component/partial/LoadingSkeleton";
import { useDocumentsContext } from "/component/provider/DocumentsProvider";
import { useToastContext } from "/component/provider/ToastProvider";
import { useTranslation } from "/hook";
import { useAnalytics } from "/hook/useAnalytics/useAnalytics";
import { replaceHtmlImgTag } from "/util/documents.util";

import { DocsWrapper, ListContainer } from "../DocumentsB2C.styles";
import DocumentListItem from "./DocumentListItem";
import DocumentsEmptyState from "./DocumentsEmptyState";
import DocumentsErrorState from "./DocumentsErrorState";
import DocumentsProviderNote from "./DocumentsProviderNote";

const DocumentsInOffice = () => {
  const { showToast } = useToastContext();
  const { t } = useTranslation("documents");
  const { recordAnalyticsEvent } = useAnalytics();
  const {
    inOfficeData: data,
    inOfficeDocs: documents,
    inOfficeError: error,
    loadingInOfficeDocs: isLoading,
    readStatusMap,
    markAsRead,
  } = useDocumentsContext();

  const showErrorState = error && getErrorCode(error, data) !== ErrorCode.PARTIAL_RESPONSE;
  const showEmptyState = (data?.patient?.letters?.totalCount || 0) <= 0;

  useEffect(() => {
    if (!isLoading) {
      recordAnalyticsEvent("viewDocuments", { noData: showEmptyState });
    }
  }, [recordAnalyticsEvent, showEmptyState, isLoading]);

  if (isLoading) return <LoadingSkeleton skeletonVariant="list-item" />;

  const sortedDocuments = [...(documents ?? [])].sort((a, b) => {
    return new Date(b.createdDatetime).getTime() - new Date(a.createdDatetime).getTime();
  });

  const handleDocumentError = () => {
    showToast({
      message: t("documentLoadError"),
      icon: "alert",
      type: "error",
    });
  };

  const handleMarkAsRead = (letter: GET_LETTERS_patient_letters_items) => {
    if (letter?.letterId) markAsRead(letter.letterId.toString());
  };

  const handlePressDocument = (letter: GET_LETTERS_patient_letters_items) => {
    recordAnalyticsEvent("viewDocument");

    const documentTitle = letter?.paperformName || "";

    if (letter.html) {
      const downloadLink = document.createElement("a");
      downloadLink.href = `data:application/pdf;base64,${letter.html}`;
      downloadLink.download = `${documentTitle}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      handleMarkAsRead(letter);
    } else if (letter.letterText) {
      // decode base64 data
      let htmlString = Base64.decode(letter.letterText || "");
      // replace unknown img with SummitHealth logo
      htmlString = replaceHtmlImgTag(htmlString);

      html2pdf()
        .set({
          fileName: documentTitle,
          margin: 10,
          html2canvas: { scale: 2, width: 825 },
        })
        .from(htmlString)

        .toPdf()
        .get("pdf")
        .then(function (pdf: { output: (arg0: string) => string | URL | undefined }) {
          window.open(pdf.output("bloburl"), "_blank");
        })
        .then(() => {
          handleMarkAsRead(letter);
        });
    } else {
      handleDocumentError();
    }
  };

  return (
    <DocsWrapper>
      <DocumentsProviderNote />
      {showErrorState ? (
        <DocumentsErrorState />
      ) : showEmptyState ? (
        <DocumentsEmptyState />
      ) : (
        <ListContainer>
          <List
            as="Buttons"
            chevronVariant="grey"
            hideLastChildBorder
            onClick={handlePressDocument}
            data={sortedDocuments}
            renderItem={(document) => (
              <DocumentListItem
                title={document.paperformName || ""}
                providerName={document.provider?.displayName || ""}
                dateString={document.createdDatetime}
                unread={!readStatusMap[document.letterId]}
              />
            )}
          />
        </ListContainer>
      )}
    </DocsWrapper>
  );
};

export default DocumentsInOffice;
